import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import {
  wordDescriptionGenres,
  wordDescriptionItems,
  type CustomWordDescriptionItem,
  type WordDescriptionGenreId,
  type WordDescriptionItem,
} from "../data/wordDescriptionItems";
import { shuffleArray } from "../utils/shuffleArray";
import {
  loadWordDescriptionStorage,
  saveWordDescriptionStorage,
} from "../utils/wordDescriptionStorage";

type GamePhase = "setup" | "playing" | "finished";
type RoundResult = {
  item: WordDescriptionItem | CustomWordDescriptionItem;
  wordCount: number;
  result: "correct" | "skipped";
};

const mainWordDescriptionGenres = wordDescriptionGenres.filter(
  (genre) => !genre.settingsOnly,
);

const getGenreById = (genreId: WordDescriptionGenreId) =>
  wordDescriptionGenres.find((genre) => genre.id === genreId) ??
  wordDescriptionGenres[0];

export default function WordDescriptionGamePage() {
  const navigate = useNavigate();
  const [initialStorage] = useState(() => loadWordDescriptionStorage());
  const [selectedGenreId, setSelectedGenreId] =
    useState<WordDescriptionGenreId>(initialStorage.selectedGenreId);
  const [customWords, setCustomWords] = useState<CustomWordDescriptionItem[]>(
    initialStorage.customWords,
  );
  const [roundSize, setRoundSize] = useState(initialStorage.roundSize);
  const [remainingItemIdsByGenre, setRemainingItemIdsByGenre] = useState(
    initialStorage.remainingItemIdsByGenre,
  );
  const [phase, setPhase] = useState<GamePhase>("setup");
  const [roundOrder, setRoundOrder] = useState<string[]>([]);
  const [roundIndex, setRoundIndex] = useState(0);
  const [roundWordCount, setRoundWordCount] = useState(0);
  const [totalWordCount, setTotalWordCount] = useState(0);
  const [roundResults, setRoundResults] = useState<RoundResult[]>([]);
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [customGenreId, setCustomGenreId] = useState<WordDescriptionGenreId>(
    initialStorage.selectedGenreId,
  );
  const [customWord, setCustomWord] = useState("");
  const [customWordError, setCustomWordError] = useState("");

  const activeGenre = getGenreById(selectedGenreId);
  const allItems = useMemo(
    () => [...wordDescriptionItems, ...customWords],
    [customWords],
  );
  const activeItems = useMemo(
    () => allItems.filter((item) => item.genreId === selectedGenreId),
    [allItems, selectedGenreId],
  );
  const itemById = useMemo(
    () => new Map(allItems.map((item) => [item.id, item])),
    [allItems],
  );
  const currentItem = itemById.get(roundOrder[roundIndex]);
  const correctCount = roundResults.filter(
    (round) => round.result === "correct",
  ).length;
  const skippedCount = roundResults.length - correctCount;

  useEffect(() => {
    saveWordDescriptionStorage({
      selectedGenreId,
      customWords,
      roundSize,
      remainingItemIdsByGenre,
    });
  }, [customWords, remainingItemIdsByGenre, roundSize, selectedGenreId]);

  const selectGenre = (genreId: WordDescriptionGenreId) => {
    setSelectedGenreId(genreId);
    setCustomGenreId(genreId);
  };

  const startGame = () => {
    const activeItemIds = activeItems.map((item) => item.id);
    const activeItemIdSet = new Set(activeItemIds);
    const storedRemainingIds = remainingItemIdsByGenre[selectedGenreId].filter(
      (itemId) => activeItemIdSet.has(itemId),
    );
    const firstPool =
      storedRemainingIds.length > 0
        ? storedRemainingIds
        : shuffleArray(activeItemIds);
    const selectedIds = firstPool.slice(0, roundSize);
    let nextRemainingIds = firstPool.slice(selectedIds.length);

    if (selectedIds.length < roundSize && activeItemIds.length > 0) {
      const selectedIdSet = new Set(selectedIds);
      const refillPool = shuffleArray(
        activeItemIds.filter((itemId) => !selectedIdSet.has(itemId)),
      );
      const refillCount = Math.min(
        roundSize - selectedIds.length,
        refillPool.length,
      );

      selectedIds.push(...refillPool.slice(0, refillCount));
      nextRemainingIds = refillPool.slice(refillCount);
    }

    const nextOrder = selectedIds;
    setRemainingItemIdsByGenre((remainingIds) => ({
      ...remainingIds,
      [selectedGenreId]: nextRemainingIds,
    }));
    setRoundOrder(nextOrder);
    setRoundIndex(0);
    setRoundWordCount(0);
    setTotalWordCount(0);
    setRoundResults([]);
    setPhase(nextOrder.length > 0 ? "playing" : "setup");
  };

  const countWord = () => {
    setRoundWordCount((count) => count + 1);
    setTotalWordCount((count) => count + 1);
  };

  const undoWord = () => {
    if (roundWordCount === 0) return;
    setRoundWordCount((count) => count - 1);
    setTotalWordCount((count) => Math.max(count - 1, 0));
  };

  const finishRound = (result: RoundResult["result"]) => {
    if (!currentItem) return;

    setRoundResults((results) => [
      ...results,
      { item: currentItem, wordCount: roundWordCount, result },
    ]);
    setRoundWordCount(0);

    if (roundIndex >= roundOrder.length - 1) {
      setPhase("finished");
      return;
    }

    setRoundIndex((index) => index + 1);
  };

  const returnToSetup = () => {
    setPhase("setup");
    setRoundOrder([]);
    setRoundIndex(0);
    setRoundWordCount(0);
    setTotalWordCount(0);
    setRoundResults([]);
  };

  const addCustomWord = () => {
    const nextWord = customWord.trim();
    if (!nextWord) {
      setCustomWordError("추가할 제시어를 입력해주세요.");
      return;
    }

    const isDuplicate = allItems.some(
      (item) =>
        item.genreId === customGenreId &&
        item.word.toLowerCase() === nextWord.toLowerCase(),
    );
    if (isDuplicate) {
      setCustomWordError("같은 장르에 이미 있는 제시어예요.");
      return;
    }

    setCustomWords((words) => [
      ...words,
      {
        id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        genreId: customGenreId,
        word: nextWord,
        createdAt: Date.now(),
      },
    ]);
    setCustomWord("");
    setCustomWordError("");
  };

  const removeCustomWord = (id: string) => {
    setCustomWords((words) => words.filter((item) => item.id !== id));
  };

  return (
    <Container maxWidth="sm">
      <Paper
        elevation={4}
        sx={{ p: { xs: 2.5, sm: 3 }, mt: 4, borderRadius: 3 }}
      >
        <Stack spacing={3}>
          <Stack spacing={1} textAlign="center">
            <Typography variant="h4">🗣️ 단어로만 설명하기</Typography>
            <Typography color="text.secondary">
              문장 없이 단어만 말해서 제시어를 맞혀보세요.
            </Typography>
          </Stack>

          {phase === "setup" && (
            <>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                <Stack spacing={2}>
                  <Stack spacing={0.5}>
                    <Typography variant="h6">장르 선택</Typography>
                    <Typography variant="body2" color="text.secondary">
                      시작 전에 한 장르를 골라주세요.
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {mainWordDescriptionGenres.map((genre) => (
                      <Button
                        key={genre.id}
                        color={genre.accentColor}
                        variant={
                          genre.id === selectedGenreId
                            ? "contained"
                            : "outlined"
                        }
                        onClick={() => selectGenre(genre.id)}
                        sx={{ flex: "1 1 calc(50% - 8px)", minWidth: 120 }}
                      >
                        {genre.emoji} {genre.label}
                      </Button>
                    ))}
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    {activeGenre.description}
                  </Typography>
                  {activeGenre.settingsOnly && (
                    <Alert severity="info">
                      커스텀 장르는 설정에서만 선택할 수 있어요. 아래 설정에서
                      진행 장르를 바꾸거나 제시어를 추가할 수 있습니다.
                    </Alert>
                  )}
                  <Chip
                    color={activeGenre.accentColor}
                    label={`전체 ${activeItems.length}개 · 이번 게임 ${Math.min(roundSize, activeItems.length)}개`}
                  />
                </Stack>
              </Paper>

              <Alert severity="info">
                설명자는 화면을 보고 연관된 단어를 하나씩 말합니다. 단어를 말할
                때마다 카운트하고, 상대가 맞히면 정답 버튼을 눌러주세요.
              </Alert>

              <Button
                fullWidth
                size="large"
                variant="contained"
                color={activeGenre.accentColor}
                onClick={startGame}
                disabled={activeItems.length === 0}
                sx={{ py: 1.5 }}
              >
                {activeGenre.label} 게임 시작
              </Button>
            </>
          )}

          {phase === "playing" && currentItem && (
            <>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  color={activeGenre.accentColor}
                  label={`${activeGenre.emoji} ${activeGenre.label}`}
                />
                <Chip
                  variant="outlined"
                  label={`ROUND ${roundIndex + 1} / ${roundOrder.length}`}
                />
                <Chip
                  variant="outlined"
                  label={`총 사용 단어 ${totalWordCount}`}
                />
              </Stack>

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderRadius: 3,
                  textAlign: "center",
                }}
              >
                <Stack spacing={1.5}>
                  <Typography variant="overline" color="text.secondary">
                    제시어
                  </Typography>
                  <Typography
                    variant="h3"
                    fontWeight={700}
                    sx={{ wordBreak: "keep-all" }}
                  >
                    {currentItem.word}
                  </Typography>
                  <Typography color="text.secondary">
                    문장이 아닌 단어만 말해주세요.
                  </Typography>
                </Stack>
              </Paper>

              <Paper sx={{ p: 2.5, borderRadius: 3, bgcolor: "action.hover" }}>
                <Stack spacing={2} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    이번 제시어에 사용한 단어
                  </Typography>
                  <Typography
                    variant="h2"
                    fontWeight={700}
                    color={`${activeGenre.accentColor}.main`}
                  >
                    {roundWordCount}
                  </Typography>
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    onClick={countWord}
                    sx={{ py: 2 }}
                  >
                    단어 +1
                  </Button>
                  <Button
                    size="small"
                    onClick={undoWord}
                    disabled={roundWordCount === 0}
                  >
                    하나 취소
                  </Button>
                </Stack>
              </Paper>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  color="success"
                  onClick={() => finishRound("correct")}
                >
                  맞혔어요
                </Button>
                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  color="inherit"
                  onClick={() => finishRound("skipped")}
                >
                  넘어가기
                </Button>
              </Stack>
            </>
          )}

          {phase === "finished" && (
            <>
              <Paper
                variant="outlined"
                sx={{ p: 3, borderRadius: 3, textAlign: "center" }}
              >
                <Stack spacing={1.5}>
                  <Typography variant="h5" fontWeight={700}>
                    게임 완료!
                  </Typography>
                  <Typography color="text.secondary">
                    {activeGenre.label} 제시어를 모두 사용했어요.
                  </Typography>
                  <Divider />
                  <Stack direction="row" justifyContent="space-around">
                    <Stack>
                      <Typography variant="h4" fontWeight={700}>
                        {correctCount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        맞힌 단어
                      </Typography>
                    </Stack>
                    <Stack>
                      <Typography variant="h4" fontWeight={700}>
                        {skippedCount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        넘긴 단어
                      </Typography>
                    </Stack>
                    <Stack>
                      <Typography variant="h4" fontWeight={700}>
                        {totalWordCount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        사용한 단어
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>

              <Accordion
                disableGutters
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600}>라운드 기록</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {roundResults.map((round, index) => (
                      <Paper
                        key={round.item.id}
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: 2 }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ROUND {index + 1}
                            </Typography>
                            <Typography fontWeight={600}>
                              {round.item.word}
                            </Typography>
                          </Stack>
                          <Chip
                            size="small"
                            color={
                              round.result === "correct" ? "success" : "default"
                            }
                            label={`${round.result === "correct" ? "정답" : "넘김"} · ${round.wordCount}개`}
                          />
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Button
                fullWidth
                size="large"
                variant="contained"
                onClick={returnToSetup}
              >
                단어로만 설명하기 시작 페이지로 이동
              </Button>
            </>
          )}

          {phase === "setup" && (
            <Accordion
              expanded={isSettingsExpanded}
              onChange={(_, expanded) => setIsSettingsExpanded(expanded)}
              disableGutters
              sx={{
                borderRadius: 3,
                bgcolor: "background.default",
                overflow: "hidden",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack spacing={0.25}>
                  <Typography fontWeight={600}>설정 · 제시어</Typography>
                  <Typography variant="body2" color="text.secondary">
                    게임당 {roundSize}개 · 내 단어 {customWords.length}개
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                    <Stack spacing={1.5}>
                      <Stack spacing={0.5}>
                        <Typography fontWeight={600}>진행 장르</Typography>
                        <Typography variant="body2" color="text.secondary">
                          커스텀 장르는 이 설정에서만 선택할 수 있습니다.
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        {wordDescriptionGenres.map((genre) => (
                          <Button
                            key={genre.id}
                            size="small"
                            color={genre.accentColor}
                            variant={
                              genre.id === selectedGenreId
                                ? "contained"
                                : "outlined"
                            }
                            onClick={() => selectGenre(genre.id)}
                            sx={{
                              flex: "1 1 calc(33.333% - 8px)",
                              minWidth: 100,
                            }}
                          >
                            {genre.settingsOnly ? "커스텀" : genre.label}
                          </Button>
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography fontWeight={600}>
                        한 게임의 제시어 개수
                      </Typography>
                      <Chip
                        size="small"
                        color="primary"
                        label={`${roundSize}개`}
                      />
                    </Stack>
                    <Slider
                      aria-label="한 게임의 제시어 개수"
                      min={1}
                      max={36}
                      step={1}
                      marks={[
                        { value: 1, label: "1" },
                        { value: 12, label: "12" },
                        { value: 24, label: "24" },
                        { value: 36, label: "36" },
                      ]}
                      value={roundSize}
                      valueLabelDisplay="auto"
                      onChange={(_, value) => setRoundSize(value as number)}
                    />
                    <Typography variant="body2" color="text.secondary">
                      기본은 12개입니다. 같은 장르를 12개씩 플레이하면 3회까지
                      겹치지 않고, 4회차부터 다시 섞여 나옵니다.
                    </Typography>
                  </Stack>
                  <Divider />
                  <Typography variant="body2" color="text.secondary">
                    장르와 제시어를 정해 추가하면 해당 장르 게임에 함께 섞여
                    나옵니다. 커스텀 장르는 직접 추가한 제시어만 사용합니다.
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    label="장르"
                    value={customGenreId}
                    onChange={(event) =>
                      setCustomGenreId(
                        event.target.value as WordDescriptionGenreId,
                      )
                    }
                  >
                    {wordDescriptionGenres.map((genre) => (
                      <MenuItem key={genre.id} value={genre.id}>
                        {genre.emoji} {genre.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    label="추가할 제시어"
                    value={customWord}
                    error={Boolean(customWordError)}
                    helperText={
                      customWordError || "예: 인공지능, 우리 동네 맛집"
                    }
                    onChange={(event) => {
                      setCustomWord(event.target.value);
                      if (customWordError) setCustomWordError("");
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") addCustomWord();
                    }}
                  />
                  <Button variant="contained" onClick={addCustomWord}>
                    내 단어 추가
                  </Button>

                  {customWords.length > 0 && <Divider />}
                  {customWords.map((item) => (
                    <Paper
                      key={item.id}
                      variant="outlined"
                      sx={{ p: 1.5, borderRadius: 2 }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Chip
                            size="small"
                            label={getGenreById(item.genreId).label}
                          />
                          <Typography sx={{ overflowWrap: "anywhere" }}>
                            {item.word}
                          </Typography>
                        </Stack>
                        <IconButton
                          aria-label={`${item.word} 삭제`}
                          onClick={() => removeCustomWord(item.id)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {phase === "playing" && (
            <Button
              fullWidth
              variant="text"
              color="inherit"
              onClick={returnToSetup}
            >
              게임 종료
            </Button>
          )}
          <Button fullWidth onClick={() => navigate("/list")}>
            게임 목록으로
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}
